import {
  ACCEPTABLE_USE_RESTRICTIONS,
  LEGAL_CONTACTS,
  LEGAL_OWNER_COUNTRY,
  LEGAL_OWNER_NAME,
  LEGAL_REVIEW_FLAGS,
  PRODUCT_NAME,
  SECURITY_COMMITMENTS,
  THIRD_PARTY_PROCESSORS,
} from "../lib/legal/config";

export type LegalSection = {
  title: string;
  body: string[];
};

export const legalReviewItems = [
  `${LEGAL_REVIEW_FLAGS.ownerDecision}: confirm legal notice, privacy, and security contact channels.`,
  `${LEGAL_REVIEW_FLAGS.ownerDecision}: confirm governing law, venue, and whether arbitration or class-action waiver language should be used.`,
  `${LEGAL_REVIEW_FLAGS.ownerDecision}: confirm refund, subscription, and enterprise contracting language before monetized launch.`,
  `${LEGAL_REVIEW_FLAGS.counselReview}: review IP ownership, feedback license, acceptable use, and limitation of liability language before production rollout.`,
  `${LEGAL_REVIEW_FLAGS.counselReview}: validate state privacy rights language and children treatment against actual marketing and customer geography.`,
];

export const termsSections: LegalSection[] = [
  {
    title: "Acceptance and product scope",
    body: [
      `${PRODUCT_NAME} is a local-first opportunity management application operated by ${LEGAL_OWNER_NAME}, a ${LEGAL_OWNER_COUNTRY} company. These Terms govern access to the application, related content, and related documentation unless a separate written agreement controls.`,
      `${PRODUCT_NAME} helps users organize opportunity signals, proof, correspondence, narrative drafts, and export packages. It does not provide legal advice, tax advice, employment law advice, mental health care, investment advice, or any other regulated professional service.`,
    ],
  },
  {
    title: "Ownership and reserved rights",
    body: [
      `${LEGAL_OWNER_NAME} retains all right, title, and interest in and to the platform and all platform intellectual property, including the application, source code, visual design system, workflows, prompts, schemas, automation logic, exported platform templates, documentation, branding, and all derivatives and improvements created by or for ${LEGAL_OWNER_NAME}.`,
      `Access to ${PRODUCT_NAME} is licensed, not sold. Except for user-owned content and third-party materials subject to their own rights, no ownership interest is transferred to the user, customer, contractor, or vendor by using the platform.`,
      `No implied license is granted. All rights not expressly granted are reserved by ${LEGAL_OWNER_NAME}.`,
    ],
  },
  {
    title: "Limited license to users",
    body: [
      `Subject to these Terms, users receive a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to access and use ${PRODUCT_NAME} for internal, lawful opportunity-management purposes.`,
      `Users may export their own workspace materials for their own lawful use, but may not copy or redistribute proprietary platform materials, protected workflows, prompts, or branded assets except as expressly permitted by the platform or a separate written agreement.`,
    ],
  },
  {
    title: "User content, uploads, and rights",
    body: [
      `Users retain ownership of content they submit, upload, or generate from their own materials to the extent they own or control those rights. Users grant ${LEGAL_OWNER_NAME} only the limited rights needed to host, process, secure, sync, transform, and export that content in order to operate the service and fulfill user-requested actions.`,
      `Users represent that they have the rights and permissions needed to submit content and that their submissions do not violate law, confidentiality obligations, privacy rights, or third-party intellectual property rights.`,
      `If a user enables AI or cloud sync, the user is directing the platform to transmit the relevant content to the configured processor for that purpose.`,
    ],
  },
  {
    title: "Feedback and improvement ideas",
    body: [
      `If a user provides suggestions, bug reports, comments, or other feedback, ${LEGAL_OWNER_NAME} may use, adapt, disclose, and commercialize that feedback without restriction or obligation, without creating any ownership transfer away from ${LEGAL_OWNER_NAME}, and without any duty to compensate the submitter unless a separate written agreement states otherwise.`,
    ],
  },
  {
    title: "Prohibited conduct",
    body: [...ACCEPTABLE_USE_RESTRICTIONS],
  },
  {
    title: "Third-party services and open source",
    body: [
      `The platform may interoperate with third-party services such as Supabase, OpenAI, Anthropic, Sentry, and user-selected identity or communication services. Those services are controlled by their own terms and privacy notices.`,
      `The platform also includes open-source software subject to the applicable upstream licenses. Those upstream licenses continue to apply to the relevant components.`,
    ],
  },
  {
    title: "AI output and professional judgment",
    body: [
      `AI-assisted outputs may be incomplete, incorrect, or misleading. Users are responsible for reviewing all generated output before using it in outreach, interviews, negotiations, compliance decisions, or other real-world actions.`,
      `${PRODUCT_NAME} is a workflow and drafting tool. Final responsibility for professional, legal, financial, employment, accessibility, and reputational decisions remains with the user and any qualified advisor they choose to consult.`,
    ],
  },
  {
    title: "Suspension, termination, and enforcement",
    body: [
      `${LEGAL_OWNER_NAME} may suspend, restrict, or terminate access, disable integrations, or block exports when reasonably necessary to protect users, the platform, third parties, or platform integrity, including for security, abuse, legal compliance, or nonpayment reasons where applicable.`,
      `Sections addressing ownership, restrictions, disclaimers, liability limits, indemnity, and dispute terms survive termination.`,
    ],
  },
  {
    title: "Disclaimers, liability limits, and indemnity",
    body: [
      `${PRODUCT_NAME} is provided on an as-is and as-available basis to the fullest extent permitted by law. ${LEGAL_OWNER_NAME} disclaims implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, and uninterrupted or error-free operation.`,
      `To the fullest extent permitted by law, ${LEGAL_OWNER_NAME} will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for lost profits, lost data, loss of business, or reputational harm arising from or related to the platform.`,
      `To the fullest extent permitted by law, users agree to indemnify and hold harmless ${LEGAL_OWNER_NAME} from third-party claims arising from user content, unlawful use, violation of these Terms, or violation of another party's rights.`,
    ],
  },
  {
    title: "Governing law, venue, and notices",
    body: [
      `${LEGAL_REVIEW_FLAGS.ownerDecision}: Governing law is currently set as ${LEGAL_CONTACTS.governingLaw}. Venue and dispute process are currently set as ${LEGAL_CONTACTS.venue}.`,
      `${LEGAL_REVIEW_FLAGS.ownerDecision}: Legal notices should be directed to ${LEGAL_CONTACTS.legalNoticeEmail} and ${LEGAL_CONTACTS.postalNotice}.`,
      `Users may not use the platform in violation of applicable export control or sanctions laws, to the extent those laws apply.`,
    ],
  },
];

export const privacySections: LegalSection[] = [
  {
    title: "Summary of actual data flows",
    body: [
      `${PRODUCT_NAME} stores workspace state locally in IndexedDB and localStorage by default. That state can include user profile data, opportunity details, uploaded or pasted proof text, correspondence drafts, AI settings, workflow events, and locally stored diagnostics preferences.`,
      `Optional cloud sync sends selected workspace records to Supabase when configured and when an authenticated user is present. Optional AI features send the relevant prompt content to the chosen AI provider only when the user enables that provider and triggers the action. Optional diagnostics send technical telemetry to Sentry only when diagnostics are enabled and a DSN is configured.`,
    ],
  },
  {
    title: "Categories of personal data",
    body: [
      `Identifiers and profile data such as name, email, phone, timezone, region, role, compensation targets, accessibility needs, and sponsorship type.`,
      `Opportunity data such as company names, role titles, job posting links, source context, lifecycle status, notes, tasks, events, and correspondence.`,
      `User-submitted content such as pasted text, PDF-extracted text, transcripts, proof summaries, private notes, and exported packages.`,
      `Sensitive support data that may include layoff status, criminal history, reentry information, or other support categories if the user chooses to record them.`,
      `Technical and diagnostics data such as runtime errors, performance traces, and local friction telemetry when diagnostics are enabled.`,
    ],
  },
  {
    title: "Sources, purposes, and processors",
    body: [
      `We collect data directly from the user, from files or text the user uploads or pastes, from voice input the user records, and from optional integrations or cloud sync settings the user chooses to enable.`,
      `We use data to operate the local workspace, generate user-requested exports, provide AI-assisted drafting and extraction, preserve user settings, secure the app, investigate technical issues, and improve product reliability.`,
      ...THIRD_PARTY_PROCESSORS.map(
        (processor) => `${processor.name}: ${processor.purpose} Data involved: ${processor.data}`,
      ),
    ],
  },
  {
    title: "Storage technologies and retention",
    body: [
      `The app uses IndexedDB and localStorage for local-first persistence. Those technologies remain on the user's device until the user clears them, resets the workspace, or uninstalls the relevant browser data.`,
      `Local diagnostics history is capped in code to recent events, but users can clear diagnostics data from the Privacy page.`,
      `If cloud sync is enabled, synced records remain subject to the customer's Supabase configuration and retention choices until deleted or replaced by the operator. ${LEGAL_REVIEW_FLAGS.counselReview}: align cloud retention with actual deployment commitments before production launch.`,
    ],
  },
  {
    title: "User choices and rights",
    body: [
      `Users can keep diagnostics disabled, choose whether to enable AI providers, choose whether to sync with Supabase, and choose whether sensitive support notes may be included in exports where account policy allows it.`,
      `Users can clear local diagnostics and local workspace data from the Privacy page.`,
      `${LEGAL_REVIEW_FLAGS.counselReview}: add a finalized rights-response workflow for applicable U.S. state privacy rights and any international rights the business elects to honor. Privacy requests should be directed to ${LEGAL_CONTACTS.privacyEmail}.`,
    ],
  },
  {
    title: "Children, international use, and security",
    body: [
      `${PRODUCT_NAME} is not directed to children under 13 and should not be used by children under 13.`,
      `Because optional providers may process data outside the user's local device, users should assume cross-border transfers can occur when they enable those providers. ${LEGAL_REVIEW_FLAGS.counselReview}: finalize transfer language based on actual vendor configuration and hosting locations.`,
      ...SECURITY_COMMITMENTS,
    ],
  },
];

export const cookieSections: LegalSection[] = [
  {
    title: "Storage and tracking technologies in this app",
    body: [
      `${PRODUCT_NAME} currently relies primarily on browser localStorage and IndexedDB rather than advertising cookies. These technologies persist local workspace state, local telemetry, and privacy preferences on the user's device.`,
      `If diagnostics are enabled, Sentry may also use its own technical storage or network mechanisms needed for error monitoring. The app does not currently include advertising pixels, session replay marketing tools, or behavioral ad targeting SDKs.`,
    ],
  },
  {
    title: "Why these technologies are used",
    body: [
      `Required local storage: workspace persistence, import and export continuity, AI settings storage, and local privacy preferences.`,
      `Optional diagnostics storage: local friction telemetry and Sentry error reporting when diagnostics are enabled.`,
    ],
  },
  {
    title: "Choices",
    body: [
      `Users can disable diagnostics and clear local diagnostics data from the Privacy page.`,
      `Users can clear local workspace data from the Privacy page or from browser settings. Clearing browser storage may remove saved workspace progress and preferences.`,
    ],
  },
];

export const securitySections: LegalSection[] = [
  {
    title: "Security posture",
    body: SECURITY_COMMITMENTS.map((item) => item),
  },
  {
    title: "Operational limits",
    body: [
      `This page describes the current product posture and engineering controls visible in the codebase. It is not a certification statement, audit report, or contractual security addendum.`,
      `The application does not currently claim SOC 2, ISO 27001, HIPAA compliance, or any regulated-data suitability. Do not rely on the app for regulated-data workloads absent a separate written agreement and technical hardening program.`,
    ],
  },
  {
    title: "Responsible disclosure",
    body: [
      `Security reports should be sent to ${LEGAL_CONTACTS.securityEmail}. ${LEGAL_REVIEW_FLAGS.ownerDecision}: replace this placeholder before public launch.`,
      `Please do not publicly disclose a vulnerability until ${LEGAL_OWNER_NAME} has had a reasonable opportunity to investigate and remediate it.`,
    ],
  },
];

export const acceptableUseSections: LegalSection[] = [
  {
    title: "Acceptable use baseline",
    body: ACCEPTABLE_USE_RESTRICTIONS.map((item) => item),
  },
  {
    title: "User responsibility for submissions",
    body: [
      `Users are responsible for ensuring that uploaded or pasted materials are lawful to use, free of malware to the extent reasonably possible, and appropriate for the workflow being performed.`,
      `Users should avoid uploading secrets, credentials, payment-card data, government identifiers, or other highly sensitive regulated data unless and until the product is explicitly hardened for that use case.`,
    ],
  },
];
